import { Button } from "@/components/ui/Button/Button";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import { InputForm } from "../ui/Input/InputForm";
import PhoneIcon from "@/assets/svgs/phone.svg?react";
import { unlockProductSchema } from "@/store/wallet/wallet.model";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useUnlockGift, useUnlockProduct, useUnlockResale } from "@/store/wallet/wallet.service";
import { DrawerHeader } from "../ServiceDrawer/ServiceDrawer";
import { Modal } from "../Modals/Modal.component";
import { Pages } from "@/utils/routes.utils";
import { useNavigate } from "react-router-dom";
import { useResponsive } from "@/utils/useResponsive";

import CheckIcon from "@/assets/svgs/check.svg?react";
import { UnlockStepper } from "./UnlockStepper";
import { useBrandConfig } from "@/utils/useBrandConfig";

type UnlockProductProps = {
  productName: string;
  productSku: string;
  image: string;
  productId: string;
  reference: string;
  gift?: string;
  resale?: string;
}

export const UnlockProduct = ({ image, productName, productSku, productId, reference, gift, resale }: UnlockProductProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  const { digitalWalletConfig: { technology } } = useBrandConfig();

  const { mutate: unlockProduct } = useUnlockProduct({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: unlockGift } = useUnlockGift({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: unlockResale } = useUnlockResale({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const {
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(unlockProductSchema()),
    mode: "onChange",
    defaultValues: {
      uid: '',
    }
  });

  const formValues = watch();

  useEffect(() => {
    if (formValues.uid) {
      trigger('uid');
    }
  }, [formValues.uid, trigger])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trigger();

    if (isValid) {
      unlock(formValues.uid);
    }
  }

  const unlock = (value: string) => {
    if (reference === value) {
      setLoading(true);
      if (gift) {
        unlockGift(gift);
      } else if (resale) {
        unlockResale(resale);
      } else {
        unlockProduct(productId);
      }
    } else {
      toast.error(t('unlock.failure'));
    }
  }

  return isMobile ? (
    <div className="pl-4 pr-6 py-5 h-full flex flex-col justify-between">
      <div>
        <p className="text-titleLarge">{t("unlock.title")}</p>
        <DrawerHeader
          image={image}
          productName={productName}
          productSku={productSku}
        />
        <UnlockStepper
          reference={reference}
          productId={productId}
          unlock={unlock}
        />
      </div>
    </div>
  ) : (
    <form onSubmit={handleSubmit} className="pl-4 pr-6 py-5 h-full flex flex-col justify-between">
      <div>
        <p className="text-titleLarge">{t("unlock.title")}</p>
        <DrawerHeader
          image={image}
          productName={productName}
          productSku={productSku}
        />
        {(technology === 'UID')
          ? <InputForm
            label={t('inputs.uid.label')}
            placeholder={t('inputs.uid.placeholder')}
            value={formValues.uid}
            handleChange={(value) => setValue('uid', value)}
            error={errors.uid?.message}
          />
          : <div className="flex flex-col gap-y-4 my-6 px-5 py-8 justify-between items-center bg-[#F7F7F7] border-[rgba(0,0,0,0.05)] border-[0.5px] rounded-sm">
            <PhoneIcon />
            <div className="text-textSmall text-center">
              <span>
                {t('unlock.info_1')}
              </span>
              <span className="text-headingXSmall">
                {' '}{t('unlock.info_2').replace('technology', technology ?? '')}
              </span>
              <span>
                {t('unlock.info_3')}
              </span>
            </div>
          </div>}
      </div>
      {(technology === 'UID') && <div className="flex justify-center items-center">
        <Button
          type="submit"
          primary
          size="large"
          label={t('buttons.unlock')}
          loading={loading}
        />
      </div>}
      <Modal
        title={t("unlock.modal.title")}
        subtitle={t("unlock.modal.subtitle")}
        open={successModalOpen}
        closeBtn
        onClose={() => {
          navigate(Pages.HOME);
        }}
        header={<CheckIcon />}
      />
    </form>
  )
}